/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import {
  ArrowBack,
  List,
  SkipPrevious,
  SkipNext,
  Close,
} from "@material-ui/icons";

import { getCurrentCourse } from "client/actions/courses";
import { getNotes, getLecsWithNotes, ApricotAction } from "client/actions/note";

import { NotesState } from "client/reducers/noteReducer";
import { LecturesWithNotes } from "client/reducers/noteReducer";
import { NoteEntryType } from "client/views/ClassroomPage/NoteEntry";
import {
  selectorNotes,
  selectorLecsWithNotes,
} from "client/selectors/noteSelector";
import { selectorCurrentCourse } from "client/selectors/courseSelector";

import { sMyNotes, sOpenNotes, sGoToLec } from "assets/jss/views/ResStrings";

import { studyNotesStyle } from "assets/jss/views/studyPage/studyNotesStyle.jsx";
const styles = createStyles(studyNotesStyle as any);

interface Props extends WithStyles<typeof styles> {
  user: any;
  notes: NotesState;
  currentCourse: any;
  lecsWithNotes: LecturesWithNotes;
  // actions
  getNotes: ApricotAction;
  getCurrentCourse: ApricotAction;
  getLecsWithNotes: ApricotAction;
  // component props
  cid: string;
  history: any;
  onClose: (exitStudy: boolean) => void;
  classes: any;
}

const MyNotesBase: React.FC<Props> = ({
  user,
  notes,
  currentCourse,
  lecsWithNotes,
  getNotes,
  getCurrentCourse,
  getLecsWithNotes,
  cid,
  history,
  onClose,
  classes,
}: Props) => {
  const [currentLec, setCurrentLec] = useState("");
  const [contentTitle, setContentTitle] = useState(sMyNotes);
  const [lecList, setLecList] = useState<
    Array<{ _id: string; title: string; seq: number }>
  >([]);

  const theme = useTheme();
  const notesColor = theme.palette.notes.main;

  const descriptionElementRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const { current: descriptionElement } = descriptionElementRef;
    if (descriptionElement !== null) {
      descriptionElement.focus();
    }
  }, []);

  useEffect(() => {
    if (cid !== "") {
      getCurrentCourse(cid);
      getLecsWithNotes({ user: user._id, course: cid });
    }
  }, [cid]);

  useEffect(() => {
    if (
      currentCourse &&
      currentCourse._id === cid &&
      lecsWithNotes &&
      lecsWithNotes.course === cid
    ) {
      let list: Array<{ _id: string; title: string; seq: number }> = [];
      currentCourse.lectures.forEach((lec: any) => {
        if (lecsWithNotes.lecList.indexOf(lec._id) !== -1) {
          const { _id, title, seq } = lec;
          list.push({ _id, title, seq });
        }
      });
      setLecList(list);
    }
  }, [currentCourse, lecsWithNotes]);

  useEffect(() => {
    if (currentLec !== "") {
      getNotes({ user: user._id, course: cid, lecture: currentLec });
      if (currentCourse) {
        const idx = currentCourse.lectures.findIndex(
          (lec: any) => lec._id === currentLec,
        );
        setContentTitle(currentCourse.lectures[idx].title);
      }
    } else {
      setContentTitle(sMyNotes);
    }
  }, [currentLec]);

  const renderLectures = () => {
    return lecList.length ? (
      lecList.map((lec: any) => (
        <div className={classes.lecLabel} key={lec._id}>
          <div className={classes.lecTitle}>
            <span style={{ fontFamily: "Open Sans" }}>{lec.seq + 1}.</span>
            &nbsp;
            {lec.title}
          </div>
          <div className={classes.lecActions}>
            <Button
              classes={{
                root: classes.notesButton,
              }}
              size="small"
              variant="contained"
              onClick={() => setCurrentLec(lec._id)}>
              {sOpenNotes}
            </Button>
            <Button
              style={{ textTransform: "none" }}
              color="secondary"
              size="small"
              variant="contained"
              onClick={() => history.push(`/clsrm/${cid}/${lec._id}`)}>
              {sGoToLec}
            </Button>
          </div>
        </div>
      ))
    ) : (
      <div style={{ fontSize: "1.5rem", margin: "5rem 2rem" }}>
        There is no notes taken in this course.
      </div>
    );
  };

  const renderNotes = () => {
    if (currentLec !== notes.curLecture) {
      return <></>;
    }

    const noteIcon = (clr: string | undefined) => (
      <span style={{ color: clr || notesColor }}>
        <i
          style={{
            minWidth: "0.8rem",
            minHeight: "0.8rem",
            backgroundColor: "transparent",
          }}
          className="far fa-sticky-note"
        />
      </span>
    );

    return (
      <div className={classes.notesDiv}>
        {notes[currentLec].map((note: NoteEntryType) => (
          <div key={note._id}>
            <p
              style={{
                fontWeight: 600,
                fontSize: "1rem",
                marginTop: "0.5rem",
              }}>
              {noteIcon(note.clr)}&nbsp;
              {note.label}
            </p>
            <p
              style={{
                fontSize: "1rem",
                whiteSpace: "pre-wrap",
                overflowWrap: "break-word",
              }}
              // render saved linebreaks
            >
              {note.text}
            </p>
          </div>
        ))}
      </div>
    );
  };

  const navButton = (
    hint: string,
    action: () => void,
    disabled: boolean,
    icon: React.ReactNode,
  ) => (
    <Tooltip title={hint}>
      <span>
        <Button
          variant="contained"
          color="primary"
          disabled={disabled}
          onClick={action}>
          {icon}
        </Button>
      </span>
    </Tooltip>
  );

  const lecControl = () => {
    const idx = lecList.findIndex((lec: any) => lec._id === currentLec);
    return (
      <>
        {navButton(
          "Back to Lecture List",
          () => setCurrentLec(""),
          false,
          <>
            <ArrowBack />
            <List />
          </>,
        )}
        {navButton(
          "Previous Lecture",
          () => setCurrentLec(lecList[idx - 1]._id),
          idx === 0,
          <SkipPrevious />,
        )}
        {navButton(
          "Next Lecture",
          () => setCurrentLec(lecList[idx + 1]._id),
          idx === lecList.length - 1,
          <SkipNext />,
        )}
      </>
    );
  };

  const renderActions = () => (
    <>
      <Button
        style={{ textTransform: "none" }}
        onClick={() => onClose(false)}
        color="secondary"
        variant="contained">
        Back to Course List
      </Button>
      {currentLec !== "" && lecControl()}
    </>
  );

  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <div>
      <Dialog
        classes={{ paper: classes.studyDlg }}
        fullScreen={fullScreen}
        fullWidth={true}
        open={true}
        onClose={() => onClose(true)}
        scroll={"paper"}>
        <DialogTitle
          id="scroll-dialog-title"
          classes={{ root: classes.dlgTitle }}
          style={{ backgroundColor: theme.palette.notes.main }}>
          <div className={classes.titleText}>
            {contentTitle}
            <IconButton color="inherit" onClick={() => onClose(true)}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent classes={{ root: classes.contentMain }} dividers={true}>
          {currentLec !== "" ? renderNotes() : renderLectures()}
        </DialogContent>
        <DialogActions>{renderActions()}</DialogActions>
      </Dialog>
    </div>
  );
};

function mapStateToProps(state: any) {
  return {
    user: state.state_auth,
    currentCourse: selectorCurrentCourse(state),
    notes: selectorNotes(state.state_notes),
    lecsWithNotes: selectorLecsWithNotes(state.state_notes),
  };
}

const base: any = MyNotesBase;
export const MyNotes: any = withStyles(styles)(
  connect(mapStateToProps, {
    getNotes,
    getCurrentCourse,
    getLecsWithNotes,
  })(base),
);

export const MyNotesTest = withStyles(styles)(MyNotesBase);
